import { Button, Group, MultiSelect, Select, Stack, Textarea } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useSchoolManagementStore } from 'store/schoolManagement';
import { SchoolDataForm } from 'types/dacum/schoolManagement';
import { UseMutationResult, useQuery } from '@tanstack/react-query';
import { courseService, JobFamilyData, JobFamilyGroupData } from 'services/lcms/course.service';

interface CreateSchoolFormProps {
  createSchool: UseMutationResult<any, Error, SchoolDataForm>;
  updateSchool: UseMutationResult<any, Error, SchoolDataForm>;
}

const SCHOOL_OPTIONS = [
  { value: 'School Area Teachers', label: 'SCH - School Area Teachers' },
  { value: 'School area operation of K-12', label: 'SO - School area operation of K-12' },
  { value: 'School of Faculty Member', label: 'FM - School of Faculty Member' },
  {
    value: 'School of Higher Education Operations',
    label: 'HEO - School of Higher Education Operations'
  },
  { value: 'School of Corporate Function', label: 'CF - School of Corporate Function' },
  { value: 'School of Core Competency', label: 'CC - School of Core Competency' },
  { value: 'School of Leadership', label: 'BL - School of Leadership' },
  { value: 'School of Culture', label: 'CU - School of Culture' },
  { value: 'Community Of Interest', label: 'COI - Community Of Interest' }
];

const resolveSchoolName = (name: string) =>
  SCHOOL_OPTIONS.find((opt) => opt.value.toLowerCase() === name?.toLowerCase())?.value || name;

const CreateSchoolForm = ({ createSchool, updateSchool }: CreateSchoolFormProps) => {
  const [selectedJobFamilyGroup, setSelectedJobFamilyGroup] = useState('');
  const navigate = useNavigate();
  const { formData, mode, setFormData } = useSchoolManagementStore();

  const { data: dataBusinessUnit, isPending: loadingBusinessUnit } = useQuery({
    queryKey: ['getBusinessUnitCreateLCMS'],
    queryFn: () => courseService.getBusinessUnitCreateLCMS()
  });
  const { data: dataJobFamilyGroups, isPending: loadingJobFamilyGroup } = useQuery({
    queryKey: ['getLCMSJobFamilyGroups'],
    queryFn: () => courseService.getLCMSJobFamilyGroups(),
    staleTime: Infinity
  });

  const { data: dataJobFamilies, isPending: loadingJobFamily } = useQuery({
    queryKey: ['getLCMSJobFamilies', selectedJobFamilyGroup],
    queryFn: () =>
      courseService.getLCMSJobFamilies({
        jobFamilyGroupID: selectedJobFamilyGroup || undefined
      }),
    staleTime: Infinity,
    enabled: !!selectedJobFamilyGroup
  });

  const {
    control,
    reset,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<SchoolDataForm>({
    //   resolver: zodResolver(coursePlanSchema),
    defaultValues: {
      description: formData.description,
      notes: formData.notes || '',
      schoolName: formData.schoolName,
      businessUnitID: formData.businessUnit || ''
    },
    mode: 'all',
    reValidateMode: 'onChange'
  });

  useEffect(() => {
    reset({
      description: formData.description,
      notes: formData.notes || '',
      schoolName: resolveSchoolName(formData.schoolName || ''),
      jobFamilyGroupID: formData.jobFamilyGroupID || '',
      listJobFamily: formData.listJobFamily || [],
      businessUnitID: formData.businessUnit || ''
    });
    setValue('businessUnitID', formData.businessUnit || '');
    setValue('description', formData.description || '');
    setValue('notes', formData.notes || '');
    setValue('jobFamilyGroupID', formData.jobFamilyGroupID || '');
    setValue('listJobFamily', formData.listJobFamily || []);
    setValue('schoolName', resolveSchoolName(formData.schoolName || ''));
    setSelectedJobFamilyGroup(formData.jobFamilyGroupID || '');
  }, [formData]);

  const onSubmit = (data: SchoolDataForm) => {
    setFormData({
      ...data,
      notes: data.notes,
      businessUnit: data.businessUnitID,
      msSchoolCompetencyManagementID: formData.msSchoolCompetencyManagementID
    });

    if (mode === 'add') {
      createSchool.mutate(data);
    } else if (mode === 'edit') {
      updateSchool.mutate(data);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={32}>
          <Stack gap={16}>
            <Controller
              name='schoolName'
              control={control}
              render={({ field }) => (
                <Select
                  label='School Name'
                  placeholder='Select school name'
                  data={SCHOOL_OPTIONS}
                  error={errors.schoolName?.message}
                  searchable
                  {...field}
                />
              )}
            />

            <Controller
              name='businessUnitID'
              control={control}
              render={({ field }) => {
                return (
                  <Select
                    label='Business Unit'
                    placeholder='Enter business unit'
                    data={[
                      ...(dataBusinessUnit?.data?.map(
                        (item: { buCode: string; buName: string }) => {
                          return {
                            label: item.buName,
                            value: item.buCode
                          };
                        }
                      ) || [])
                    ]}
                    disabled={loadingBusinessUnit}
                    error={errors.businessUnitID?.message}
                    searchable
                    {...field}
                  />
                );
              }}
            />

            <Controller
              name='jobFamilyGroupID'
              control={control}
              render={({ field }) => {
                return (
                  <Select
                    label='Job Family Group'
                    placeholder='Enter job family group'
                    data={
                      dataJobFamilyGroups?.data?.map((item: JobFamilyGroupData) => ({
                        value: item.jobFamilyGroupID,
                        label: item.jobFamilyGroupName
                      })) || []
                    }
                    disabled={loadingJobFamilyGroup}
                    error={errors.jobFamilyGroupID?.message}
                    searchable
                    {...field}
                    onChange={(val) => {
                      field.onChange(val);
                      setSelectedJobFamilyGroup(val || '');
                      setValue('listJobFamily', []);
                    }}
                  />
                );
              }}
            />

            <Controller
              name='listJobFamily'
              control={control}
              render={({ field }) => {
                return (
                  <MultiSelect
                    label='Job Family'
                    placeholder='Enter job family'
                    data={
                      dataJobFamilies?.data?.map((item: JobFamilyData) => ({
                        value: item.jobFamilyID,
                        label: item.jobFamilyName
                      })) || []
                    }
                    disabled={!selectedJobFamilyGroup || loadingJobFamilyGroup || loadingJobFamily}
                    error={errors.listJobFamily?.message}
                    searchable
                    {...field}
                  />
                );
              }}
            />

            <Controller
              name='description'
              control={control}
              render={({ field }) => (
                <Textarea
                  label='Description (optional)'
                  placeholder='Enter description'
                  error={errors.description?.message}
                  minRows={3}
                  disabled={mode === 'view'}
                  {...field}
                />
              )}
            />
            <Controller
              name='notes'
              control={control}
              render={({ field }) => (
                <Textarea
                  label='Note (optional)'
                  placeholder='Enter note'
                  error={errors.notes?.message}
                  minRows={3}
                  disabled={mode === 'view'}
                  {...field}
                />
              )}
            />
          </Stack>

          <Group justify='flex-end'>
            <Button variant='outline' onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button
              type='submit'
              loading={isSubmitting || createSchool.isPending || updateSchool.isPending}
              size='md'
              leftSection={<IconDeviceFloppy />}
            >
              {mode === 'add' ? 'Create' : 'Update'} School
            </Button>
          </Group>
        </Stack>
      </form>
    </>
  );
};

export default CreateSchoolForm;
